import httpClient from "@/services/httpClient";

export const getRequest = async (requestId) => {
  try {
    const response = await httpClient.get(`/api/v1/requests/${requestId}`);
    if (response.status !== 200) {
      throw new Error("Failed to fetch request");
    }
    return response.data?.request || null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const suggestedRequests = async () => {
  try {
    const response = await httpClient.get("/api/v1/recommended_request_categories");
    if (!response.data?.success) {
      throw new Error("Failed to fetch suggested requests");
    }
    const categories = response.data.recommended_request_categories.reduce(
      (acc, category) => {
        const requestSurveys = category.request_surveys || [];

        // TODO: Remove this when we have switched everyone over to the new surveys
        // and removed the old surveys
        const filteredRequestSurveys = requestSurveys.filter(
          (survey) => survey.for_alpha,
        );

        acc.push({
          ...category,
          request_surveys: filteredRequestSurveys.slice(0, 3),
        });

        return acc;
      },
      [],
    );
    return categories;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const openRequests = async () => {
  try {
    const response = await httpClient.get("/api/v1/requests/open");
    if (response.status !== 200) {
      throw new Error("Failed to fetch open requests");
    }

    return (
      response.data?.requests?.map((request) => ({
        ...request,
        status: request.status || "In Queue",
      })) || []
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const inProgressRequests = async () => {
  try {
    const response = await httpClient.get("/api/v1/requests/in_progress");
    if (response.status !== 200) {
      throw new Error("Failed to fetch in progress requests");
    }

    return (
      response.data?.requests?.map((request) => ({
        ...request,
        status: request.status || "In Progress",
      })) || []
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const closedRequests = async () => {
  try {
    const response = await httpClient.get("/api/v1/requests/completed");
    if (response.status !== 200) {
      throw new Error("Failed to fetch closed requests");
    }
    return (
      response.data?.requests.map((request) => ({
        ...request,
        status: request.status || "Completed",
      })) || []
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const beginRequestSurvey = async (requestSurveyId) => {
  try {
    const response = await httpClient.post(
      `/api/v1/request_surveys/${requestSurveyId}/user_request_surveys`,
      {
        for_alpha: true,
      },
    );

    if (response.status !== 200 || !response.data?.success) {
      throw new Error("Failed to fetch request survey");
    }
    return response.data?.user_request_survey;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const getNextQuestion = async (userRequestSurveyId) => {
  try {
    const response = await httpClient.get(
      `/api/v1/user_request_surveys/${userRequestSurveyId}/next_screen_item`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to fetch next question");
    }

    if (response.data?.next_item === null) {
      return { surveyComplete: true };
    }

    const question = {
      ...response.data?.next_item?.item?.question,
      optional: response.data?.next_item?.optional,
      id: response.data?.next_item?.item?.id,
      finalQuestion: response.data?.next_item?.last_question,
      initialValue: response.data.next_item.item?.universal_answer,
    };
    return question;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const submitAnswer = async (userRequestSurveyId, userResponse) => {
  try {
    const response = await httpClient.post(
      `/api/v1/user_request_surveys/${userRequestSurveyId}/user_request_answers`,
      {
        user_request_answer: {
          object_satisfied_id: userResponse.questionId,
          object_satisfied_type: "Datum",
          value: userResponse.answer,
        },
      },
    );
    if (response.status !== 200 || !response.data?.success) {
      throw new Error("Failed to submit answer");
    }
    return response.data?.user_request_answer;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const completeSurvey = async (userRequestSurveyId) => {
  try {
    const response = await httpClient.post(
      `/api/v1/user_request_surveys/${userRequestSurveyId}/submit`,
    );
    if (!response.data?.success) {
      throw new Error("Failed to complete survey");
    }

    return response.data;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const allCategories = async () => {
  try {
    const response = await httpClient.get("/api/v1/survey_categories");
    if (response.status !== 200) {
      throw new Error("Failed to fetch categories");
    }
    return response.data?.survey_categories || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getCategoryRequests = async (categoryId) => {
  try {
    const response = await httpClient.get(
      `/api/v1/survey_categories/${categoryId}/request_surveys`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to fetch category requests");
    }

    const surveyCategory = response.data?.survey_category;
    const requestSurveys = (response.data?.request_surveys || []).filter(
      (survey) => survey.for_alpha,
    );
    return { ...surveyCategory, requestSurveys: requestSurveys };
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getUploadData = async (filename) => {
  try {
    const response = await httpClient.post("/api/v1/uploads/presigned_url", {
      upload: { filename },
    });

    const { presigned_url, key } = response.data;
    return { presignedUrl: presigned_url, key };
  } catch (error) {
    console.error(error);
    return null;
  }
};
